import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useOrganization } from './organizations';

export interface FormField {
  id: string;
  label: string;
  type: 'text' | 'textarea' | 'select' | 'multi_select' | 'radio' | 'checkbox' | 'file';
  required: boolean;
  placeholder?: string;
  options?: string[];
}

export interface IntakeFormSection {
  id: string;
  org_id: string;
  section_key: string;
  section_name: string;
  sort_order: number;
  enabled: boolean;
  fields: FormField[];
  created_at: string;
  updated_at: string;
}

// Default sections for new orgs
export const defaultSections: Omit<IntakeFormSection, 'id' | 'org_id' | 'created_at' | 'updated_at'>[] = [
  {
    section_key: 'brand_info',
    section_name: 'Brand Info',
    sort_order: 1,
    enabled: true,
    fields: [
      { id: 'brandName', label: 'Brand / Company Name', type: 'text', required: true, placeholder: 'Your brand name' },
      { id: 'contactName', label: 'Your Name', type: 'text', required: true, placeholder: 'Your full name' },
      { id: 'contactEmail', label: 'Email', type: 'text', required: true, placeholder: 'you@company.com' },
      { id: 'instagram', label: 'Instagram Handle', type: 'text', required: false, placeholder: '@handle' },
      { id: 'website', label: 'Website', type: 'text', required: false, placeholder: 'https://...' },
      { id: 'references', label: 'Brand Aesthetic References', type: 'file', required: false },
    ],
  },
  {
    section_key: 'project_needs',
    section_name: 'Project Needs',
    sort_order: 2,
    enabled: true,
    fields: [
      { id: 'projectType', label: 'Project Type', type: 'radio', required: true, options: ['Film', 'Photo', 'Both'] },
      { id: 'campaignType', label: 'Campaign Type', type: 'select', required: true, options: ['Product Launch', 'Brand Story', 'Documentary', 'Campaign Launch', 'Event Coverage', 'Commercial', 'Music Video', 'Other'] },
      { id: 'projectDescription', label: 'Project Description', type: 'textarea', required: false, placeholder: 'Tell us about your vision...' },
      { id: 'deliverables', label: 'Deliverables Needed', type: 'multi_select', required: false, options: ['Hero Film (60s+)', 'Social Cuts (15-30s)', 'Behind the Scenes', 'Photography (Product)', 'Photography (Lifestyle)', 'Raw Footage', 'Motion Graphics', 'Sound Design', 'Color Grading'] },
    ],
  },
  {
    section_key: 'budget_timeline',
    section_name: 'Budget & Timeline',
    sort_order: 3,
    enabled: true,
    fields: [
      { id: 'budgetRange', label: 'Budget Range', type: 'select', required: true, options: ['$5,000 - $10,000', '$10,000 - $25,000', '$25,000 - $50,000', '$50,000 - $100,000', '$100,000+'] },
      { id: 'preferredTimeline', label: 'Preferred Timeline', type: 'text', required: false, placeholder: 'e.g., Need by June 2024' },
      { id: 'urgency', label: 'Urgency Level', type: 'radio', required: false, options: ['Flexible', 'Standard', 'Rush'] },
    ],
  },
];

export const useIntakeFormConfig = () => {
  const { data: org } = useOrganization();

  return useQuery({
    queryKey: ['intake-form-config', org?.id],
    queryFn: async () => {
      if (!org?.id) return null;

      const { data, error } = await supabase
        .from('intake_form_config')
        .select('*')
        .eq('org_id', org.id)
        .order('sort_order');

      if (error) throw error;
      
      // Parse fields from JSONB
      return (data || []).map(section => ({
        ...section,
        fields: Array.isArray(section.fields) ? section.fields as unknown as FormField[] : [],
      })) as IntakeFormSection[];
    },
    enabled: !!org?.id,
  });
};

export const useIntakeFormConfigByOrgId = (orgId: string | undefined) => {
  return useQuery({
    queryKey: ['intake-form-config', orgId],
    queryFn: async () => {
      if (!orgId) return null;

      const { data, error } = await supabase
        .from('intake_form_config')
        .select('*')
        .eq('org_id', orgId)
        .order('sort_order');

      if (error) throw error;
      
      return (data || []).map(section => ({
        ...section,
        fields: Array.isArray(section.fields) ? section.fields as unknown as FormField[] : [],
      })) as IntakeFormSection[];
    },
    enabled: !!orgId,
  });
};

export const useUpsertIntakeFormConfig = () => {
  const queryClient = useQueryClient();
  const { data: org } = useOrganization();

  return useMutation({
    mutationFn: async (sections: Omit<IntakeFormSection, 'id' | 'created_at' | 'updated_at'>[]) => {
      if (!org?.id) throw new Error('Your studio isn’t set up yet');

      // Delete existing sections for this org, then insert new ones
      const { error: deleteError } = await supabase
        .from('intake_form_config')
        .delete()
        .eq('org_id', org.id);

      if (deleteError) throw deleteError;

      const { data, error } = await supabase
        .from('intake_form_config')
        .insert(sections.map(s => ({
          org_id: org.id,
          section_key: s.section_key,
          section_name: s.section_name,
          sort_order: s.sort_order,
          enabled: s.enabled,
          fields: s.fields as any,
        })))
        .select();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['intake-form-config', org?.id] });
    },
  });
};

export const useInitializeDefaultConfig = () => {
  const queryClient = useQueryClient();
  const { data: org } = useOrganization();

  return useMutation({
    mutationFn: async () => {
      if (!org?.id) throw new Error('Your studio isn’t set up yet');

      const { data, error } = await supabase
        .from('intake_form_config')
        .insert(defaultSections.map(s => ({
          org_id: org.id,
          section_key: s.section_key,
          section_name: s.section_name,
          sort_order: s.sort_order,
          enabled: s.enabled,
          fields: s.fields as any,
        })))
        .select();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['intake-form-config', org?.id] });
    },
  });
};
